import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, Calendar, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const categories = ['Todos', ...new Set(posts.map(post => post.category))];
  
  const filteredPosts = selectedCategory === 'Todos' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div className="industrial-bg" data-testid="blog-page">
      {/* Hero */}
      <section className="bg-[#1A365D] py-16">
        <div className="section-container">
          <nav className="text-sm text-gray-400 mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-white">Inicio</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-white">Blog</li>
            </ol>
          </nav>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-4">
            Blog de Pladur y Construcción
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Guías, consejos y novedades sobre yesos, pladur y materiales de construcción. 
            Conocimiento profesional para profesionales.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-gray-200 sticky top-[120px] z-30">
        <div className="section-container py-4">
          <div className="flex gap-3 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-[#1A365D] text-white'
                    : 'bg-gray-100 text-[#4A5568] hover:bg-gray-200'
                }`}
                data-testid={`category-filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="section-container">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A365D] mx-auto"></div>
              <p className="mt-4 text-[#4A5568]">Cargando artículos...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#4A5568]">No hay artículos en esta categoría.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.slug}
                  className="bg-white rounded-sm border border-gray-200 overflow-hidden card-hover group"
                  data-testid={`blog-post-card-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-[#64748B] mb-3">
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.read_time} min
                        </span>
                      </div>
                      <h2 className="font-heading text-xl font-bold text-[#1A365D] mb-3 line-clamp-2 group-hover:text-[#3182CE] transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-[#4A5568] text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-sm text-[#64748B]">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.published_date).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="text-[#3182CE] font-medium text-sm group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                          Leer más <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1A365D]">
        <div className="section-container text-center">
          <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
            ¿Necesitas Material para tu Proyecto?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            En Fornes e Hijos tenemos todo lo que necesitas para tus instalaciones 
            de pladur, yesos y aislamientos en Motril.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/productos"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1A365D] font-semibold px-6 py-3 rounded-sm hover:bg-gray-100 transition-colors"
              data-testid="blog-cta-productos"
            >
              Ver Productos
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors"
              data-testid="blog-cta-contacto"
            >
              Contactar
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
